import { useMessageTemplates, fillTemplate, WEDDING_TEMPLATES } from '../lib/messageTemplates';
import { openMessageAfter } from '../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataStore } from '../store/dataStore';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Phone,
  Mail,
  Calendar,
  Clock,
  MapPin,
  Camera,
  Video,
  Plane,
  TreeDeciduous,
  Film,
  BookOpen,
  CreditCard,
  Banknote,
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save,
  Image,
  FolderOpen,
  Send,
  MessageSquare,
  Copy,
  UserCog,
  ShieldCheck
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

// Hizmet seçenekleri
const serviceOptions = [
  { key: 'service_photo', label: 'Fotoğraf Çekimi', icon: Camera },
  { key: 'service_video', label: 'Video Çekimi', icon: Video },
  { key: 'service_drone', label: 'Drone Çekimi', icon: Plane },
  { key: 'service_outdoor', label: 'Dış Çekim', icon: TreeDeciduous },
  { key: 'service_clip', label: 'Klip Çekimi', icon: Film },
  { key: 'service_story', label: 'Hikaye Çekimi', icon: BookOpen },
];

// Ödeme türleri
const paymentTypes = [
  { value: 'cash', label: 'Nakit' },
  { value: 'credit_card', label: 'Kredi Kartı' },
  { value: 'bank_transfer', label: 'Havale/EFT' },
  { value: 'other', label: 'Diğer' },
];

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { venues, fetchVenues, fetchCustomers, updateCustomer } = useDataStore();
  const { token, user } = useAuthStore();
  const tpl = useMessageTemplates('/api', token, WEDDING_TEMPLATES);
  const isTeamMember = user?.role === 'team_member';
  
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teamList, setTeamList] = useState([]);
  
  // Collapsible states
  const [openSections, setOpenSections] = useState({
    personal: true,
    wedding: true,
    events: true,
    services: true,
    pricing: true,
    payments: true,
    messaging: true
  });
  
  // Payment dialog
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_type: 'cash',
    notes: ''
  });
  
  // Custom message state
  const [customMessage, setCustomMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  
  // Event edit/delete state
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_date: '',
    event_time_start: '',
    event_time_end: '',
    location: '',
    services: [],
    notes: ''
  });
  
  // Borç ve Log state
  const [isDebtDialogOpen, setIsDebtDialogOpen] = useState(false);
  const [debtForm, setDebtForm] = useState({ amount: '', notes: '' });
  const [logs, setLogs] = useState([]);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    // Token kontrolü - yeni sayfa yüklenirken bekle
    if (!token) {
      // Token yoksa login sayfasına yönlendir
      navigate('/giris');
      return;
    }
    fetchVenues();
    loadCustomer();
    loadPayments();
    loadTeam();
  }, [customerId, token]);

  const loadTeam = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTeamList(await res.json());
    } catch (_) {}
  };

  const loadCustomer = async () => {
    try {
      if (!token) {
        navigate('/giris');
        return;
      }
      const res = await fetch(`${API}/api/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Müşteri bulunamadı');
      }
      const data = await res.json();
      setCustomer(data);
      setEditForm(data);
    } catch (error) {
      toast.error(error.message);
      navigate('/panel/musteriler');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const res = await fetch(`${API}/api/payments/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Ödemeler yüklenemedi:', error);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        bride_name: editForm.bride_name,
        groom_name: editForm.groom_name,
        bride_phone: editForm.bride_phone,
        groom_phone: editForm.groom_phone,
        email: editForm.email,
        wedding_date: editForm.wedding_date,
        wedding_time_start: editForm.wedding_time_start,
        wedding_time_end: editForm.wedding_time_end,
        venue_id: editForm.venue_id || null,
        notes: editForm.notes,
        service_photo: editForm.service_photo || false,
        service_video: editForm.service_video || false,
        service_drone: editForm.service_drone || false,
        service_outdoor: editForm.service_outdoor || false,
        service_clip: editForm.service_clip || false,
        service_story: editForm.service_story || false,
        custom_services: editForm.custom_services || '',
        total_price: parseFloat(editForm.total_price) || 0,
        deposit_amount: parseFloat(editForm.deposit_amount) || 0,
        assigned_team: editForm.assigned_team || []
      };
      
      const res = await fetch(`${API}/api/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) throw new Error('Güncellenemedi');
      
      const updated = await res.json();
      setCustomer(updated);
      setIsEditing(false);
      toast.success('Müşteri güncellendi');
      fetchCustomers(); // Listeyi güncelle
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Geçerli bir tutar girin');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: customerId,
          amount: parseFloat(paymentForm.amount),
          payment_type: paymentForm.payment_type,
          notes: paymentForm.notes
        })
      });
      
      if (!res.ok) throw new Error('Ödeme eklenemedi');
      
      toast.success('Ödeme eklendi');
      setIsPaymentDialogOpen(false);
      setPaymentForm({ amount: '', payment_type: 'cash', notes: '' });
      loadPayments();
      loadCustomer(); // Kalan tutarı güncelle
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Event silme
  const handleDeleteEvent = async (eventId, index) => {
    if (!window.confirm('Bu çekim gününü silmek istediğinize emin misiniz?')) return;
    
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Çekim günü silinemedi');
      
      toast.success('Çekim günü silindi');
      loadCustomer();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Event düzenleme dialogunu aç
  const handleEditEvent = (event, index) => {
    setEditingEvent({ ...event, index });
    setEventForm({
      event_date: event.event_date || '',
      event_time_start: event.event_time_start || '',
      event_time_end: event.event_time_end || '',
      location: event.location || '',
      services: event.services || [],
      notes: event.notes || ''
    });
    setIsEventDialogOpen(true);
  };

  // Event güncelle
  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    
    setIsSubmitting(true);
    try {
      // Önce sil, sonra yeniden ekle (basit yaklaşım)
      await fetch(`${API}/api/customers/${customerId}/events/${editingEvent.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      await fetch(`${API}/api/customers/${customerId}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_date: eventForm.event_date,
          event_time_start: eventForm.event_time_start,
          event_time_end: eventForm.event_time_end,
          location: eventForm.location,
          services: eventForm.services,
          notes: eventForm.notes
        })
      });
      
      toast.success('Çekim günü güncellendi');
      setIsEventDialogOpen(false);
      setEditingEvent(null);
      loadCustomer();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Borç ekleme
  const handleAddDebt = async () => {
    if (!debtForm.amount) {
      toast.error('Borç tutarı giriniz');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/debts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseFloat(debtForm.amount),
          notes: debtForm.notes
        })
      });
      
      if (!res.ok) throw new Error('Borç eklenemedi');
      
      toast.success('Borç eklendi');
      setIsDebtDialogOpen(false);
      setDebtForm({ amount: '', notes: '' });
      loadPayments();
      loadCustomer();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Log kayıtlarını yükle
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${API}/api/customers/${customerId}/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Loglar yüklenemedi:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
    
    try {
      const res = await fetch(`${API}/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Ödeme silinemedi');
      
      toast.success('Ödeme silindi');
      loadPayments();
      loadCustomer();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendAgreement = async (channel) => {
    if (channel !== 'copy') {
      try {
        await openMessageAfter(channel, async () => {
          const r = await fetch(`${API}/api/notifications/send-agreement`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ customer_id: customerId, channel })
          });
          if (!r.ok) throw new Error('Mesaj hazırlanamadı');
          const d = await r.json();
          return { phone: d.to, text: d.content };
        });
        toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} açıldı — anlaşma mesajı hazır`);
      } catch (error) { toast.error(error.message); }
      return;
    }
    try {
      const res = await fetch(`${API}/api/notifications/send-agreement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: customerId,
          channel: channel
        })
      });
      
      if (!res.ok) throw new Error('Mesaj gönderilemedi');
      const data = await res.json();
      // Kopyala özelliği
      if (channel === 'copy') {
        const text = data.content || '';
        try {
          await navigator.clipboard.writeText(text);
          toast.success('Anlaşma metni panoya kopyalandı');
        } catch {
          // Fallback: textarea ile kopya
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.top = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          toast.success('Anlaşma metni panoya kopyalandı');
        }
        return;
      }
      toast.success(`Anlaşma mesajı gönderildi (${channel.toUpperCase()})`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendCustomMessage = async (channel) => {
    if (!customMessage.trim()) {
      toast.error('Lütfen bir mesaj yazın');
      return;
    }
    
    setIsSendingMessage(true);
    try {
      await openMessageAfter(channel, async () => {
        const res = await fetch(`${API}/api/notifications/send-custom-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            customer_id: customerId,
            channel: channel,
            message: customMessage
          })
        });
        if (!res.ok) throw new Error('Mesaj hazırlanamadı');
        return { phone: customer?.bride_phone || customer?.groom_phone, text: customMessage };
      });
      toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} açıldı — mesaj hazır, göndere basın`);
      setCustomMessage(''); // Mesajı temizle
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentTypeLabel = (type) => {
    const found = paymentTypes.find(p => p.value === type);
    return found ? found.label : type;
  };

  // Calculate totals
  const totalPrice = customer?.total_price || 0;
  const depositAmount = customer?.deposit_amount || 0;
  const paidAmount = customer?.paid_amount || 0;
  const remainingAmount = totalPrice - depositAmount - paidAmount;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/panel/musteriler')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {customer.bride_name} & {customer.groom_name}
            </h1>
            <p className="text-muted-foreground">Müşteri Detayları</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => { setIsEditing(false); setEditForm(customer); }}>
                İptal
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Kaydet</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => navigate(`/panel/album/${customerId}`)}>
                {customer.has_album ? <Image className="h-4 w-4 mr-2" /> : <FolderOpen className="h-4 w-4 mr-2" />}
                Albüm
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4 mr-2" /> Düzenle
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Personal Info */}
          <Collapsible open={openSections.personal} onOpenChange={() => toggleSection('personal')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Kişi Bilgileri
                    </span>
                    {openSections.personal ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Gelin Adı</Label>
                          <Input
                            value={editForm.bride_name || ''}
                            onChange={(e) => setEditForm({ ...editForm, bride_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Damat Adı</Label>
                          <Input
                            value={editForm.groom_name || ''}
                            onChange={(e) => setEditForm({ ...editForm, groom_name: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tel 1</Label>
                          <Input
                            value={editForm.bride_phone || ''}
                            onChange={(e) => setEditForm({ ...editForm, bride_phone: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tel 2</Label>
                          <Input
                            value={editForm.groom_phone || ''}
                            onChange={(e) => setEditForm({ ...editForm, groom_phone: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Tel 1:</span>
                        <span>{customer.bride_phone}</span>
                      </div>
                      {customer.groom_phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Tel 2:</span>
                          <span>{customer.groom_phone}</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Çekim Günleri */}
          <Collapsible open={openSections.events} onOpenChange={() => toggleSection('events')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Çekim Günleri ({customer.events?.length || 0})
                    </span>
                    {openSections.events ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {customer.events && customer.events.length > 0 ? (
                    customer.events.map((event, index) => (
                      <div key={event.id || index} className="p-4 rounded-lg bg-muted/30 border relative group">
                        {/* Silme ve Düzenleme Butonları */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleEditEvent(event, index)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteEvent(event.id, index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        {/* Tarih */}
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="font-medium">{formatDate(event.event_date)}</span>
                        </div>
                        
                        {/* Saat */}
                        {(event.event_time_start || event.event_time_end) && (
                          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{event.event_time_start || '--:--'} - {event.event_time_end || '--:--'}</span>
                          </div>
                        )}
                        
                        {/* Mekan */}
                        {event.location && (
                          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        
                        {/* Hizmetler */}
                        {event.services && event.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {event.services.map((service, i) => (
                              <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Not */}
                        {event.notes && (
                          <p className="text-sm text-muted-foreground mt-2 pt-2 border-t">
                            {event.notes}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-4">Henüz çekim günü eklenmemiş</p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Ekip Ataması - Ekip üyesine gösterme */}
          {!isTeamMember && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserCog className="h-5 w-5 text-primary" />
                  Ekip Ataması
                  {(customer?.assigned_team?.length > 0) && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                      {customer.assigned_team.length}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  teamList.length > 0 ? (
                    <div className="space-y-2">
                      {teamList.map(m => {
                        const isSel = (editForm.assigned_team || []).includes(m.id);
                        return (
                          <label key={m.id}
                            className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition ${isSel ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'}`}
                            data-testid={`detail-team-${m.id}`}>
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={(e) => {
                                const cur = editForm.assigned_team || [];
                                setEditForm({
                                  ...editForm,
                                  assigned_team: e.target.checked
                                    ? [...cur, m.id]
                                    : cur.filter(x => x !== m.id)
                                });
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{m.name}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {m.role === 'photographer' && 'Fotoğrafçı'}
                                {m.role === 'videographer' && 'Kameraman'}
                                {m.role === 'drone_operator' && 'Drone Op.'}
                                {m.role === 'assistant' && 'Asistan'}
                                {m.can_login && ' · Giriş açık'}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                      <p className="text-[10px] text-muted-foreground mt-2">
                        Seçilenler bu müşteriyi kendi hesaplarından görebilir (fiyat/ödeme gizli).
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      Henüz ekip üyesi yok. Ekip sayfasından ekleyebilirsiniz.
                    </p>
                  )
                ) : (
                  <>
                    {customer?.assigned_team && customer.assigned_team.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {customer.assigned_team.map(mid => {
                          const m = teamList.find(t => t.id === mid);
                          return m ? (
                            <div key={mid} className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-sm">
                              <UserCog className="h-3 w-3 text-primary" />
                              <span className="font-medium">{m.name}</span>
                              {m.can_login && <ShieldCheck className="h-3 w-3 text-emerald-600" />}
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        Bu müşteriye henüz ekip atanmamış. Düzenle butonundan atayabilirsiniz.
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pricing - Ekip üyesine gösterme */}
          {!isTeamMember && (
          <Collapsible open={openSections.pricing} onOpenChange={() => toggleSection('pricing')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-primary" />
                      Ücret Bilgileri
                    </span>
                    {openSections.pricing ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Toplam Tutar (TL)</Label>
                        <Input
                          type="number"
                          value={editForm.total_price || ''}
                          onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Kapora (TL)</Label>
                        <Input
                          type="number"
                          value={editForm.deposit_amount || ''}
                          onChange={(e) => setEditForm({ ...editForm, deposit_amount: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-muted/50">
                          <p className="text-sm text-muted-foreground">Toplam Tutar</p>
                          <p className="text-2xl font-bold">{totalPrice.toLocaleString('tr-TR')} TL</p>
                        </div>
                        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                          <p className="text-sm text-blue-600 dark:text-blue-400">Kapora</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{depositAmount.toLocaleString('tr-TR')} TL</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                          <p className="text-sm text-green-600 dark:text-green-400">Ödenen</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{paidAmount.toLocaleString('tr-TR')} TL</p>
                        </div>
                        <div className="p-4 rounded-lg bg-primary/10 border-2 border-primary">
                          <p className="text-sm text-primary">Kalan Tutar</p>
                          <p className="text-2xl font-bold text-primary">{remainingAmount.toLocaleString('tr-TR')} TL</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          )}

          {/* Payments - Ekip üyesine gösterme */}
          {!isTeamMember && (
          <Collapsible open={openSections.payments} onOpenChange={() => toggleSection('payments')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Banknote className="h-5 w-5 text-primary" />
                      Ödemeler
                      {payments.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                          {payments.length}
                        </span>
                      )}
                    </span>
                    {openSections.payments ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setIsPaymentDialogOpen(true)}
                      className="flex-1"
                      data-testid="add-payment-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Ödeme Ekle
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsDebtDialogOpen(true)}
                      className="flex-1"
                      data-testid="add-debt-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Borç Ekle (Hizmet veya Düzenleme)
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => { loadLogs(); setIsLogDialogOpen(true); }}
                    className="w-full"
                    data-testid="view-logs-btn"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    İşlem Geçmişi
                  </Button>

                  {payments.length > 0 ? (
                    <div className="space-y-3">
                      {payments.map((payment) => (
                        <div
                          key={payment.id}
                          className="p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <p className="font-semibold text-lg text-green-600 dark:text-green-400">
                                +{payment.amount.toLocaleString('tr-TR')} TL
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {getPaymentTypeLabel(payment.payment_type)}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDateTime(payment.created_at)}
                              </p>
                              {payment.notes && (
                                <p className="text-sm text-muted-foreground mt-2 italic">
                                  "{payment.notes}"
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeletePayment(payment.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Banknote className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Henüz ödeme kaydı yok</p>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          )}

          {/* Messaging Section - Ekip üyesine gösterme */}
          {!isTeamMember && (
          <Collapsible open={openSections.messaging} onOpenChange={() => toggleSection('messaging')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      Mesaj Gönder
                    </span>
                    {openSections.messaging ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {/* Agreement Template */}
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Anlaşma Şablonu Gönder
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Hazır anlaşma mesajı şablonunu müşteriye gönderin.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleSendAgreement('sms')} className="flex-1 min-w-[100px]" data-testid="agreement-sms-btn">
                        <Send className="h-4 w-4 mr-2" />
                        SMS
                      </Button>
                      <Button size="sm" onClick={() => handleSendAgreement('whatsapp')} className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700" data-testid="agreement-whatsapp-btn">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleSendAgreement('copy')} className="flex-1 min-w-[100px]" data-testid="agreement-copy-btn">
                        <Copy className="h-4 w-4 mr-2" />
                        Kopyala
                      </Button>
                    </div>
                  </div>
                  
                  {/* Custom Message */}
                  <div className="p-4 rounded-lg border">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Özel Mesaj Gönder
                    </h4>
                    <div className="space-y-3">
                      <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" defaultValue="" data-testid="custom-message-template-select"
                        onChange={(e) => {
                          if (!e.target.value || !customer) return;
                          setCustomMessage(fillTemplate(tpl.get(e.target.value), {
                            gelin: customer.bride_name, damat: customer.groom_name,
                            tarih: customer.wedding_date ? new Date(customer.wedding_date).toLocaleDateString('tr-TR') : '',
                            saat: customer.wedding_time_start || '', mekan: customer.venue_name || customer.location || '',
                            kalan_odeme: (remainingAmount || 0).toLocaleString('tr-TR'), firma: user?.company_name || user?.full_name, link: '',
                          }));
                          e.target.value = '';
                        }}>
                        <option value="">Şablondan seç (Ayarlar'dan düzenlenebilir)...</option>
                        {WEDDING_TEMPLATES.filter(t => t.key !== 'galeri').map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                      <Textarea
                        placeholder="Mesajınızı buraya yazın..."
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        rows={3}
                        data-testid="custom-message-input"
                      />
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleSendCustomMessage('sms')} 
                          disabled={isSendingMessage || !customMessage.trim()}
                          className="flex-1"
                          data-testid="send-sms-btn"
                        >
                          {isSendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                          SMS
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleSendCustomMessage('whatsapp')} 
                          disabled={isSendingMessage || !customMessage.trim()}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          data-testid="send-whatsapp-btn"
                        >
                          {isSendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                          WhatsApp
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        * Mesajlar şu an demo modunda çalışmaktadır
                      </p>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ödeme Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tutar (TL) *</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Ödeme Türü</Label>
              <Select
                value={paymentForm.payment_type}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Not (opsiyonel)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Ödeme notu..."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleAddPayment} disabled={isSubmitting} className="flex-1" data-testid="confirm-payment-btn">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ekle'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Düzenleme Dialog */}
      <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Çekim Günü Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tarih</Label>
              <Input
                type="date"
                value={eventForm.event_date}
                onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Başlangıç</Label>
                <Input
                  type="time"
                  value={eventForm.event_time_start}
                  onChange={(e) => setEventForm({ ...eventForm, event_time_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş</Label>
                <Input
                  type="time"
                  value={eventForm.event_time_end}
                  onChange={(e) => setEventForm({ ...eventForm, event_time_end: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Yer / Mekan</Label>
              <Input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Çekim yeri..."
              />
            </div>
            <div className="space-y-2">
              <Label>Hizmetler</Label>
              <div className="grid grid-cols-2 gap-2">
                {['Fotoğraf', 'Video', 'Drone', 'Dış Çekim', 'Klip', 'Hikaye'].map((service) => (
                  <label key={service} className="flex items-center gap-2 p-2 rounded border hover:bg-muted/50 cursor-pointer">
                    <Checkbox
                      checked={eventForm.services.includes(service)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setEventForm({ ...eventForm, services: [...eventForm.services, service] });
                        } else {
                          setEventForm({ ...eventForm, services: eventForm.services.filter(s => s !== service) });
                        }
                      }}
                    />
                    <span className="text-sm">{service}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notlar</Label>
              <Textarea
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
                placeholder="Bu gün için notlar..."
                rows={2}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsEventDialogOpen(false)}>
                İptal
              </Button>
              <Button className="flex-1" onClick={handleUpdateEvent} disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Borç Ekleme Dialog */}
      <Dialog open={isDebtDialogOpen} onOpenChange={setIsDebtDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Borç Ekle (Hizmet veya Düzenleme)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Ek hizmet veya düzenleme borcu ekleyin. Toplam tutara otomatik eklenir.
            </p>
            <div className="space-y-2">
              <Label>Tutar (TL) *</Label>
              <Input
                type="number"
                value={debtForm.amount}
                onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })}
                placeholder="0"
                data-testid="debt-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Input
                value={debtForm.notes}
                onChange={(e) => setDebtForm({ ...debtForm, notes: e.target.value })}
                placeholder="Borç açıklaması..."
                data-testid="debt-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsDebtDialogOpen(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleAddDebt} disabled={isSubmitting} className="flex-1" data-testid="confirm-debt-btn">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ekle'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* İşlem Geçmişi Dialog */}
      <Dialog open={isLogDialogOpen} onOpenChange={setIsLogDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>İşlem Geçmişi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : logs.length > 0 ? (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="p-3 rounded-lg border bg-muted/30">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium text-sm">
                          {log.action === 'debt_added' ? 'Borç Eklendi' : 
                           log.action === 'payment_added' ? 'Ödeme Eklendi' : 
                           log.action}
                        </p>
                        <p className="text-sm text-muted-foreground">{log.details}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateTime(log.timestamp)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          İşlemi yapan: {log.user_name}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>Henüz işlem kaydı yok</p>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setIsLogDialogOpen(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
